import { useContext, useEffect, useState } from 'react';
import { Input } from '@acko-tech/building-blocks.ui.input';
import { PrimaryButton } from '@acko-tech/building-blocks.ui.button';
import { NewPlanStages } from '../constants';
import {
    SegmentEventNameTypes,
    SegmentEventButtonNames,
    SegmentEventActionTypes,
    SegmentEventActionNames,
    SegmentEventPages,
    SegmentEventPageTypes,
} from 'utils/segment/constants';
import { MPosContext } from 'utils/context';
import { useResponsiveDevice } from 'hooks/useResponsiveDevice';
import SnackBar from 'library/snackbar';
import * as Styles from '../vehicleDetails/styles';

// utils
import { getFieldActionName } from './helpers';
import { isEmpty, isAnyKeyEmpty } from 'utils/generics';
import sendSegmentEvent from 'utils/segment';

function CustomerDetails() {
    const { planDetails, setPlanDetails, setActiveStage, userDetails } =
        useContext(MPosContext);
    const { isMobile } = useResponsiveDevice();

    const [formData, setFormData] = useState({
        name: planDetails.customer.name,
        email: planDetails.customer.email,
        mobile: planDetails.customer.phone,
        address: planDetails.customer.address,
        pincode: planDetails.customer.pincode,
    });
    const [validationErr, setValidationErr] = useState({
        name: '',
        email: '',
        mobile: '',
        address: '',
        pincode: '',
    });
    const [isContinueButtonDisabled, setIsContinueButtonDisabled] =
        useState(true);
    const [snackbar, setSnackbar] = useState({
        type: '',
        message: '',
    });

    useEffect(() => {
        if (typeof window !== 'undefined') {
            sendSegmentEvent(
                SegmentEventNameTypes.VIEW,
                {
                    page_type: SegmentEventPageTypes.CUSTOMER_DETAILS_PAGE,
                    view_name: SegmentEventPages.VIEW_CUSTOMER_DETAILS_PAGE,
                },
                {
                    userDetails,
                    isMobile,
                }
            );
        }
    }, [window]);

    function validator() {
        return {
            name: (val) => {
                if (!/^[a-zA-Z ]{2,60}$/.test(val))
                    setValidationErr({
                        ...validationErr,
                        name: 'Please enter valid name',
                    });
                else setValidationErr({ ...validationErr, name: '' });
            },
            email: (val) => {
                if (
                    !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(
                        val.trim()
                    )
                )
                    setValidationErr({
                        ...validationErr,
                        email: 'Please enter valid email',
                    });
                else setValidationErr({ ...validationErr, email: '' });
            },
            mobile: (val) => {
                if (!/^[6-9]\d{9}$/i.test(val?.toString()?.trim()))
                    setValidationErr({
                        ...validationErr,
                        mobile: 'Please enter valid mobile number',
                    });
                else setValidationErr({ ...validationErr, mobile: '' });
            },
            pincode: (val) => {
                if (!/^[1-9][0-9]{5}$/i.test(val?.toString()?.trim()))
                    setValidationErr({
                        ...validationErr,
                        pincode: 'Please enter valid pincode',
                    });
                else
                    setValidationErr({
                        ...validationErr,
                        pincode: '',
                    });
            },
            address: (val) => {
                if (val.length < 4)
                    setValidationErr({
                        ...validationErr,
                        address: 'Please enter valid address',
                    });
                else
                    setValidationErr({
                        ...validationErr,
                        address: '',
                    });
            },
        };
    }
    useEffect(() => {
        if (isEmpty(validationErr) && !isAnyKeyEmpty(formData)) {
            setIsContinueButtonDisabled(false);
        } else {
            setIsContinueButtonDisabled(true);
        }
    }, [formData]);

    function formHandler(e, name) {
        const { value } = e.target;
        validator()?.[name](value);
        setFormData({ ...formData, [name]: value });
    }

    const onSubmit = () => {
        setPlanDetails({
            ...planDetails,
            customer: {
                ...planDetails.customer,
                phone: formData.mobile,
                pincode: formData.pincode,
                address: formData.address,
                name: formData.name,
                email: formData.email,
            },
        });
        setActiveStage(NewPlanStages.REVIEW_DETAILS);

        // segment events
        sendSegmentEvent(
            SegmentEventNameTypes.ACTION,
            {
                action_type: SegmentEventActionTypes.TAP_BTN,
                action_name:
                    SegmentEventActionNames.CLICK_CONTINUE_CUSTOMER_DETAILS,
                button_name:
                    SegmentEventButtonNames.CONTINUE_FROM_CUSTOMER_DETAILS,
            },
            { userDetails, isMobile }
        );
    };

    const onFieldClick = (fieldName: string) => {
        const actionName = getFieldActionName(fieldName);
        sendSegmentEvent(
            SegmentEventNameTypes.ACTION,
            {
                action_type: SegmentEventActionTypes.ENTER_TEXT,
                action_name: actionName,
            },
            { userDetails, isMobile }
        );
    };

    return (
        // prettier-ignore
        <Styles.Wrapper style={{ position: 'relative', height: '100%',  minHeight: '500px' }}>
            <Styles.ScanIMEINumber>
                {'Enter your customer details'}
            </Styles.ScanIMEINumber>
            <Styles.InputWrapper onClick={()=>onFieldClick('name')}>
                <Input
                    className={''}
                    labelText={'Full name'}
                    name={'name'}
                    type={'text'}
                    value={formData.name}
                    onChange={(e) => formHandler(e, 'name')}
                    errMsg={validationErr.name}
                    hasError={validationErr.name.length > 0}
                />
            </Styles.InputWrapper>
            <Styles.InputWrapper onClick={()=>onFieldClick('mobile')}>
                <Input
                    className={''}
                    labelText={`Mobile No.`}
                    name={'mobile'}
                    type={'number'}
                    value={formData.mobile}
                    onChange={(e) => formHandler(e, 'mobile')}
                    errMsg={validationErr.mobile}
                    hasError={validationErr.mobile.length > 0}
                />
            </Styles.InputWrapper>
            <Styles.InputWrapper onClick={()=>onFieldClick('email')}>
                <Input
                    className={''}
                    labelText={`Email ID`}
                    name={'email'}
                    type={'text'}
                    value={formData.email}
                    onChange={(e) => formHandler(e, 'email')}
                    errMsg={validationErr.email}
                    hasError={validationErr.email.length > 0}
                />
            </Styles.InputWrapper>
            <Styles.InputWrapper onClick={() => onFieldClick('address')}>
                <Input
                    className={''}
                    labelText={'Address'}
                    name={'address'}
                    type={'text'}
                    value={formData.address}
                    onChange={(e) => formHandler(e, 'address')}
                    errMsg={validationErr.address}
                    hasError={validationErr.address.length > 0}
                />
            </Styles.InputWrapper>
            <Styles.InputWrapper onClick={() => onFieldClick('pincode')}>
                <Input
                    className={''}
                    labelText={'Pin Code'}
                    name={'pincode'}
                    type={'number'}
                    value={formData.pincode}
                    onChange={(e) => formHandler(e, 'pincode')}
                    errMsg={validationErr.pincode}
                    hasError={validationErr.pincode.length > 0}
                />
            </Styles.InputWrapper>
            <Styles.ButtonWrapper style={{ position: 'absolute', bottom: '0', width: '100%' }}>
                <PrimaryButton
                    disabled={isContinueButtonDisabled}
                    onClick={onSubmit}
                >
                    Continue
                </PrimaryButton>
            </Styles.ButtonWrapper>
            <SnackBar
                {...snackbar}
                handleSnakbarComplete={() =>
                    setSnackbar({ type: '', message: '' })
                }
            />
        </Styles.Wrapper>
    );
}

export default CustomerDetails;
